import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Download,
  Loader2,
} from "lucide-react";
import * as adminApi from "@/api/admin";
import type { QuestionnaireAnswer, QuestionnaireSubmission } from "@/api/admin";
import type { WorkBook } from "xlsx";

interface SubmissionRow extends QuestionnaireSubmission {
  pageSlug: string;
  pageTitle: string;
}

function toRow(s: QuestionnaireSubmission): SubmissionRow {
  return { ...s, pageSlug: s.page_slug, pageTitle: s.page_title };
}
function answerOfType(row: SubmissionRow, type: QuestionnaireAnswer["field_type"]) {
  return row.answers.find((a) => a.field_type === type)?.value ?? "";
}

// Checkbox labels can contain links (e.g. "accept the <a>terms</a>").
function plainLabel(label: string) {
  return label.replace(/<[^>]*>/g, "");
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

// Shared by the on-screen list and the Excel export so both show the same thing.
function formatAnswer({ field_type, value }: QuestionnaireAnswer): string {
  if (!value) return "";
  if (field_type === "checkbox") return value === "true" ? "Yes" : "No";
  if (field_type === "multiple_select") {
    try {
      const items = JSON.parse(value);
      if (Array.isArray(items)) return items.join(", ");
    } catch {
      // Fall through and show the raw value.
    }
  }
  return value;
}

function AnswerValue({ answer }: { answer: QuestionnaireAnswer }) {
  const text = formatAnswer(answer);
  if (!text) return <span className="text-gray-400">—</span>;

  if (answer.field_type === "email") {
    return <a href={`mailto:${answer.value}`} className="hover:underline">{text}</a>;
  }
  if (answer.field_type === "phone") {
    return <a href={`tel:${answer.value}`} className="hover:underline">{text}</a>;
  }
  return <>{text}</>;
}

// ---------------------------------------------------------------------------
// Excel export — an "All submissions" sheet first (when more than one page is
// exported), then one sheet per landing page, one column per question.
// ---------------------------------------------------------------------------

const SUBMITTED_AT = "Submitted at";
const SUBMISSION_ID = "Submission #";
const LANDING_PAGE = "Landing page";
const ALL_SHEET = "All submissions";

type SheetRecord = Record<string, string | number>;

function localDateStamp() {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Excel sheet names: max 31 chars, no []:*?/\ and unique (case-insensitive).
function uniqueSheetName(title: string, used: Set<string>) {
  const base = title.replace(/[[\]:*?/\\]/g, "").trim().slice(0, 31) || "Sheet";
  let name = base;
  for (let n = 2; used.has(name.toLowerCase()); n++) {
    const suffix = ` (${n})`;
    name = base.slice(0, 31 - suffix.length) + suffix;
  }
  used.add(name.toLowerCase());
  return name;
}

// Maps each field id to its column header for one landing page, in order of
// first appearance; repeated labels get a suffix so they don't collide.
function columnsForPage(pageRows: SubmissionRow[]) {
  const columns = new Map<number, string>();
  const usedHeaders = new Set([SUBMITTED_AT, SUBMISSION_ID, LANDING_PAGE]);
  pageRows.forEach((row) =>
    row.answers.forEach((answer) => {
      if (columns.has(answer.field)) return;
      const base = plainLabel(answer.field_label).trim() || `Field ${answer.field}`;
      let header = base;
      for (let n = 2; usedHeaders.has(header); n++) header = `${base} (${n})`;
      usedHeaders.add(header);
      columns.set(answer.field, header);
    }),
  );
  return columns;
}

function recordFor(row: SubmissionRow, columns: Map<number, string>, withPage: boolean): SheetRecord {
  const record: SheetRecord = { [SUBMITTED_AT]: formatDate(row.created_at) };
  if (withPage) record[LANDING_PAGE] = row.pageTitle;
  record[SUBMISSION_ID] = row.id;
  row.answers.forEach((answer) => {
    record[columns.get(answer.field)!] = formatAnswer(answer);
  });
  return record;
}

function appendSheet(
  XLSX: typeof import("xlsx"),
  workbook: WorkBook,
  name: string,
  headers: string[],
  data: SheetRecord[],
) {
  // Headers are passed explicitly, so questions a submission didn't answer
  // (or a page didn't ask) are left as blank cells.
  const sheet = XLSX.utils.json_to_sheet(data, { header: headers });
  sheet["!cols"] = headers.map((header) => ({
    wch: Math.min(50, Math.max(header.length, ...data.map((d) => String(d[header] ?? "").length)) + 2),
  }));
  XLSX.utils.book_append_sheet(workbook, sheet, name);
}

async function exportToExcel(rows: SubmissionRow[], filename: string) {
  const XLSX = await import("xlsx");
  const workbook = XLSX.utils.book_new();
  const usedSheetNames = new Set<string>();

  const byPage = new Map<string, SubmissionRow[]>();
  rows.forEach((row) => {
    byPage.set(row.pageSlug, [...(byPage.get(row.pageSlug) ?? []), row]);
  });
  const pages = [...byPage.entries()].map(([slug, pageRows]) => ({
    slug,
    pageRows,
    columns: columnsForPage(pageRows),
  }));

  // Combined sheet: same-named questions from different pages share a column.
  if (pages.length > 1) {
    const columnsBySlug = new Map(pages.map(({ slug, columns }) => [slug, columns]));
    const questionHeaders = [...new Set(pages.flatMap(({ columns }) => [...columns.values()]))];
    appendSheet(
      XLSX,
      workbook,
      uniqueSheetName(ALL_SHEET, usedSheetNames),
      [SUBMITTED_AT, LANDING_PAGE, SUBMISSION_ID, ...questionHeaders],
      rows.map((row) => recordFor(row, columnsBySlug.get(row.pageSlug)!, true)),
    );
  }

  for (const { pageRows, columns } of pages) {
    appendSheet(
      XLSX,
      workbook,
      uniqueSheetName(pageRows[0]!.pageTitle, usedSheetNames),
      [SUBMITTED_AT, SUBMISSION_ID, ...columns.values()],
      pageRows.map((row) => recordFor(row, columns, false)),
    );
  }

  XLSX.writeFile(workbook, filename);
}

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

function Pagination({
  page,
  pageCount,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
}: {
  page: number;
  pageCount: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}) {
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-500">
      <div className="flex items-center gap-2">
        <span>Rows per page</span>
        <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
          <SelectTrigger className="h-8 w-20 bg-white border-gray-200 text-gray-700 shadow-sm hover:bg-gray-50 focus-visible:ring-gray-200 focus-visible:border-gray-300">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white border-gray-200 text-gray-700">
            {PAGE_SIZE_OPTIONS.map((size) => (
              <SelectItem key={size} value={String(size)} className="focus:bg-gray-100 focus:text-gray-900">
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-3">
        <span>
          {from}–{to} of {total}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            aria-label="Previous page"
          >
            <ChevronLeft size={16} />
          </Button>
          <span className="min-w-16 text-center text-gray-700">
            {page} / {pageCount}
          </span>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= pageCount}
            aria-label="Next page"
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

export function QuestionnaireSubmissionsView({
  onCountChange,
}: {
  /** Reports the total number of matching submissions (null until loaded or on error). */
  onCountChange?: (count: number | null) => void;
}) {
  const [rows, setRows] = useState<SubmissionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [landingPages, setLandingPages] = useState<{ slug: string; title: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [pageFilter, setPageFilter] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const landingPage = pageFilter === "all" ? undefined : pageFilter;

  // Options for the landing-page filter.
  useEffect(() => {
    adminApi
      .listLandingPages()
      .then((pages) => setLandingPages(pages.map(({ slug, title }) => ({ slug, title }))))
      .catch(() => {
        // The filter just shows "All landing pages"; the list itself still works.
      });
  }, []);

  // One request per page of results.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    adminApi
      .listQuestionnaireSubmissions({ page, pageSize, landingPage })
      .then((data) => {
        if (cancelled) return;
        setRows(data.results.map(toRow));
        setTotal(data.count);
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, landingPage]);

  const count = error || !loaded ? null : total;
  useEffect(() => {
    onCountChange?.(count);
  }, [count, onCountChange]);

  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  function changeFilter(value: string) {
    setPageFilter(value);
    setPage(1);
    setExpanded(null);
  }

  function changePage(next: number) {
    setPage(next);
    setExpanded(null);
  }

  function changePageSize(size: number) {
    setPageSize(size);
    setPage(1);
    setExpanded(null);
  }

  async function handleExport() {
    setExporting(true);
    try {
      // Exports every submission matching the filter, not just the current page.
      const all = await adminApi.listAllQuestionnaireSubmissions({ landingPage });
      const scope = landingPage ? `-${landingPage}` : "";
      await exportToExcel(all.map(toRow), `questionnaires${scope}-${localDateStamp()}.xlsx`);
    } catch {
      toast.error("Export failed", { description: "Please try again." });
    } finally {
      setExporting(false);
    }
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>Failed to load questionnaire submissions. Please refresh and try again.</AlertDescription>
      </Alert>
    );
  }

  if (!loaded) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-14 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (total === 0 && !landingPage) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-400">
        <ClipboardList size={36} className="mb-3 opacity-30" />
        <p className="text-sm">No questionnaire submissions yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Select value={pageFilter} onValueChange={changeFilter}>
          <SelectTrigger className="w-full sm:w-64 bg-white border-gray-200 text-gray-700 shadow-sm hover:bg-gray-50 focus-visible:ring-gray-200 focus-visible:border-gray-300">
            <SelectValue />
          </SelectTrigger>
          <SelectContent align="start" className="bg-white border-gray-200 text-gray-700">
            <SelectItem value="all" className="focus:bg-gray-100 focus:text-gray-900">
              All landing pages
            </SelectItem>
            {landingPages.map(({ slug, title }) => (
              <SelectItem key={slug} value={slug} className="focus:bg-gray-100 focus:text-gray-900">
                {title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          onClick={handleExport}
          disabled={exporting || total === 0}
          className="bg-[#00d9d9] hover:bg-[#00bfc0] text-white"
        >
          {exporting ? <Loader2 className="animate-spin" /> : <Download />}
          Export to Excel
        </Button>
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: Math.min(pageSize, 5) }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="py-12 text-center text-sm text-gray-400">No submissions for this landing page yet.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => {
            const isOpen = expanded === row.id;
            const name = [answerOfType(row, "first_name"), answerOfType(row, "last_name")]
              .filter(Boolean)
              .join(" ");
            const email = answerOfType(row, "email");
            const phone = answerOfType(row, "phone");
            return (
              <div key={row.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                <button
                  onClick={() => setExpanded(isOpen ? null : row.id)}
                  className="w-full flex items-center gap-4 px-5 py-3.5 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="shrink-0 text-gray-400">
                    {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium text-sm text-gray-900 truncate">
                        {name || `Submission #${row.id}`}
                      </span>
                      {(email || phone) && (
                        <>
                          <span className="text-gray-300 text-xs shrink-0">·</span>
                          <span className="text-xs text-gray-500 truncate">
                            {[email, phone].filter(Boolean).join(" · ")}
                          </span>
                        </>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 truncate mt-0.5">{row.pageTitle}</p>
                  </div>
                  <span className="text-xs text-gray-400 shrink-0">{formatDate(row.created_at)}</span>
                </button>

                {isOpen && (
                  <div className="border-t border-gray-100 px-5 py-4 bg-gray-50/50">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                      {row.answers.map((answer) => (
                        <div key={answer.id} className={answer.field_type === "checkbox" ? "sm:col-span-2" : undefined}>
                          <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">
                            {plainLabel(answer.field_label)}
                          </span>
                          <p className="text-gray-800 mt-0.5 break-words">
                            <AnswerValue answer={answer} />
                          </p>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-400 mt-4">
                      Submission #{row.id} · /{row.pageSlug}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {total > 0 && (
        <Pagination
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          total={total}
          onPageChange={changePage}
          onPageSizeChange={changePageSize}
        />
      )}
    </div>
  );
}
